import { useMemo, useState } from "react";
import { FiFilter, FiX, FiRefreshCw, FiSearch } from "react-icons/fi";
import { toPersianNumber } from "../../utils/persian";
import { getColorCode } from "../../utils/colorCodes";



const splitValues = (value) => {
  if (!value) return [];
  return value.split(",").map((item) => item.trim()).filter(Boolean);
};

const joinValues = (values) => values.filter(Boolean).join(",");

const ProductFilters = ({ filters, setFilters, filterOptions, count = 0, open, setOpen }) => {
  const selectedBrands = splitValues(filters.brand);
  const selectedColors = splitValues(filters.color);
  const selectedSizes = splitValues(filters.size);

  const toggleMulti = (key, value) => {
    const current = splitValues(filters[key]);
    const exists = current.includes(value);
    const next = exists ? current.filter((item) => item !== value) : [...current, value];

    setFilters((prev) => ({
      ...prev,
      page: 1,
      [key]: joinValues(next)
    }));
  };

  const updateDirect = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      page: 1,
      [key]: value
    }));
  };

  const clear = () => {
    setFilters({
      q: filters.q || "",
      category: filters.fixedCategory || "",
      fixedCategory: filters.fixedCategory || "",
      brand: "",
      color: "",
      size: "",
      min_price: "",
      max_price: "",
      available: "",
      sort: "latest",
      page: 1
    });
  };

  const activeCount = [
    ...selectedBrands,
    ...selectedColors,
    ...selectedSizes,
    filters.min_price,
    filters.max_price,
    filters.available
  ].filter(Boolean).length;

  const brands = useMemo(() => filterOptions?.brands || [], [filterOptions?.brands]);
  const colors = useMemo(() => filterOptions?.colors || [], [filterOptions?.colors]);
  const sizes = useMemo(() => filterOptions?.sizes || [], [filterOptions?.sizes]);

  const [colorSearch, setColorSearch] = useState("");
  const [colorExpanded, setColorExpanded] = useState(false);

  const filteredColors = useMemo(() => {
    const keyword = colorSearch.trim().toLowerCase();

    if (!keyword) return colors;

    return colors.filter((item) => String(item.color || "").toLowerCase().includes(keyword));
  }, [colors, colorSearch]);

  const selectedColorItems = colors.filter((item) => selectedColors.includes(item.color));
  const visibleColors = colorExpanded || colorSearch
    ? filteredColors
    : [
        ...selectedColorItems,
        ...filteredColors.filter((item) => !selectedColors.includes(item.color))
      ].slice(0, 8);

  const hiddenColorCount = Math.max(filteredColors.length - visibleColors.length, 0);

  const content = (
    <div className="shop-filter-card" role="region" aria-label="فیلتر محصولات">
      <div className="shop-filter-head">
        <div>
          <span>فیلتر محصولات</span>
          <strong>{toPersianNumber(count)} محصول قابل نمایش</strong>
        </div>

        <button type="button" onClick={() => setOpen(false)} className="shop-filter-close" aria-label="بستن فیلتر">
          <FiX size={21} />
        </button>
      </div>

      {activeCount > 0 && (
        <div className="shop-active-filters">
          <span>{toPersianNumber(activeCount)} فیلتر فعال</span>
          <button type="button" onClick={clear}>
            <FiRefreshCw size={14} />
            پاک‌کردن
          </button>
        </div>
      )}

      {brands.length > 0 && (
        <div className="shop-filter-section">
          <label>برند</label>
          <div className="shop-chip-list">
            {brands.map((brand) => {
              const value = brand.slug || brand.name;
              return (
                <button
                  type="button"
                  key={brand.id || value}
                  onClick={() => toggleMulti("brand", value)}
                  className={selectedBrands.includes(value) ? "active" : ""}
                >
                  {brand.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {colors.length > 0 && (
        <div className="shop-filter-section shop-filter-section-colors">
          <div className="shop-filter-label-row">
            <label>رنگ</label>
            <span>{toPersianNumber(colors.length)} رنگ</span>
          </div>

          <div className="shop-color-search">
            <FiSearch size={15} />
            <input
              type="text"
              value={colorSearch}
              onChange={(e) => setColorSearch(e.target.value)}
              placeholder="جستجوی رنگ"
              aria-label="جستجوی رنگ محصول"
            />
            {colorSearch && (
              <button type="button" onClick={() => setColorSearch("")} aria-label="پاک کردن جستجوی رنگ">
                <FiX size={15} />
              </button>
            )}
          </div>

          <div className="shop-color-list shop-color-list-compact">
            {visibleColors.map((item) => (
              <button
                type="button"
                key={item.color}
                onClick={() => toggleMulti("color", item.color)}
                className={selectedColors.includes(item.color) ? "active" : ""}
              >
                <i style={{ background: getColorCode(item.color) }} />
                <span>{item.color}</span>
              </button>
            ))}
          </div>

          {!colorSearch && filteredColors.length > 8 && (
            <button
              type="button"
              className="shop-color-more-btn"
              onClick={() => setColorExpanded((prev) => !prev)}
            >
              {colorExpanded ? "نمایش کمتر" : `نمایش ${toPersianNumber(hiddenColorCount)} رنگ دیگر`}
            </button>
          )}
        </div>
      )}

      {sizes.length > 0 && (
        <div className="shop-filter-section">
          <label>سایز</label>
          <div className="shop-size-list">
            {sizes.map((item) => (
              <button
                type="button"
                key={item.size}
                onClick={() => toggleMulti("size", item.size)}
                className={selectedSizes.includes(item.size) ? "active" : ""}
              >
                {toPersianNumber(item.size)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="shop-filter-section">
        <label>بازه قیمت</label>

        <div className="shop-price-hint">
          از {toPersianNumber(filterOptions?.min_price || 0)} تا {toPersianNumber(filterOptions?.max_price || 0)} تومان
        </div>

        <div className="shop-price-range">
          <input
            type="number"
            value={filters.min_price}
            onChange={(e) => updateDirect("min_price", e.target.value)}
            placeholder="حداقل"
            aria-label="حداقل قیمت"
          />

          <input
            type="number"
            value={filters.max_price}
            onChange={(e) => updateDirect("max_price", e.target.value)}
            placeholder="حداکثر"
            aria-label="حداکثر قیمت"
          />
        </div>
      </div>

      <button
        type="button"
        className={`shop-stock-toggle ${filters.available === "1" ? "active" : ""}`}
        onClick={() => updateDirect("available", filters.available === "1" ? "" : "1")}
      >
        فقط کالاهای موجود
      </button>

      <div className="shop-filter-actions">
        <button
          type="button"
          className="shop-filter-reset-btn"
          onClick={clear}
        >
          پاک کردن
        </button>

        <button
          type="button"
          className="shop-filter-submit-btn"
          onClick={() => setOpen(false)}
        >
          مشاهده {toPersianNumber(count)} محصول
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button type="button" className="product-mobile-filter-btn" onClick={() => setOpen(true)}>
        <FiFilter size={18} />
        فیلتر محصولات
        {activeCount > 0 && <span>{toPersianNumber(activeCount)}</span>}
      </button>

      <aside className="product-filter-desktop">{content}</aside>

      <div className={`product-filter-mobile ${open ? "active" : ""}`}>
        <div className="product-filter-backdrop" onClick={() => setOpen(false)} />
        <div className="product-filter-drawer">{content}</div>
      </div>
    </>
  );
};

export default ProductFilters;
